/**
 * WishlistProductGrid Web Component
 *
 * Displays and manages wishlisted products from Swym Wishlist API.
 * Integrates with Shopify Storefront API and Cart API for product data and cart operations.
 *
 * APIs Used:
 * - Swym Wishlist API: Fetch, add, and remove wishlist items
 * - Shopify Storefront API (GraphQL): Fetch product data including variants, prices, images
 * - Shopify Cart API: Add products to cart
 *
 * Methods:
 * - init(): Initialize component settings and fetch wishlists
 * - events(): Attach event listeners for remove, edit, and add to cart buttons
 * - showLoader() / hideLoader(): Display/hide loading spinner
 * - fetchSwymLists(): Register Swym callback to initialize wishlist data
 * - initializeSwymLists(swat): Fetch wishlists using Swym API
 * - renderLists(lists): Find latest list by timestamp and render products
 * - renderProducts(products): Fetch product data from Storefront API for wishlist items
 * - renderProductGrid(products): Render product cards with images, prices, badges, and buttons
 * - formatPrice(amount, currencyCode): Format price using Intl.NumberFormat
 * - getSelectedVariant(data, epi): Find variant by ID from product data
 * - getVariantText(variant): Generate variant option text display
 * - renderBadges(data, minPrice, compareAtPrice): Render product badges
 * - calculateDiscount(comparePrice, price, type, currencyCode): Calculate discount percentage or amount
 * - renderEmptyState(): Display empty wishlist message
 * - openEditModal(button): Open product-variant-modal for editing wishlist item
 * - saveWishlistChanges(variant, productData, context): Save variant changes to Swym wishlist
 * - addToCart(button): Add product to cart via Shopify Cart API
 * - showErrorMessage(message, button): Display error message below product card
 * - removeProduct(button): Remove product from Swym wishlist
 */

class WishlistProductGrid extends HTMLElement {
  constructor() {
    super();

    this.init();
  }

  init() {
    this.settings = {
      badgePosition: this.dataset.badgePosition,
      showSoldOutBadge: this.dataset.showSoldOutBadge === 'true',
      showDiscountBadge: this.dataset.showDiscountBadge === 'true',
      saleBadgeType: this.dataset.saleBadgeType,
      cardLabelText: this.dataset.cardLabelText,
      labelSoldOut: this.dataset.labelSoldOut,
      labelOnSale: this.dataset.labelOnSale,
      labelSave: this.dataset.labelSave,
      btnEdit: this.dataset.btnEdit || 'EDIT',
      btnAdd: this.dataset.btnAdd || 'ADD',
      btnSoldOut: this.dataset.btnSoldOut || 'SOLD OUT',
      btnUnavailable: this.dataset.btnUnavailable || 'UNAVAILABLE',
    };

    this.siteSettings = window.SHOPIFY_DATA;

    this.showLoader();
    this.fetchSwymLists();
    this.events();
  }

  showLoader() {
    this.innerHTML = `
      <div class="wishlist-loader">
        <div class="loading__spinner">
          <svg
            aria-hidden="true"
            focusable="false"
            class="spinner"
            viewBox="0 0 66 66"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
          </svg>
        </div>
      </div>
    `;
  }

  hideLoader() {
    const loader = this.querySelector('.wishlist-loader');
    if (loader) {
      loader.remove();
    }
  }

  fetchSwymLists() {
    if (window.SwymCallbacks) {
      window.SwymCallbacks.push((swat) => {
        this.initializeSwymLists(swat);
      });
    } else {
      window.SwymCallbacks = [(swat) => {
        this.initializeSwymLists(swat);
      }];
    }
  }

  initializeSwymLists(swat) {
    this.swat = _swat;

    const onSuccess = (lists) => {
      this.hideLoader();
      this.renderLists(lists);
    };

    const onError = (error) => {
      console.error("Error while fetching all Lists", error);
      this.hideLoader();
    };

    _swat.fetchLists({
      callbackFn: onSuccess,
      errorFn: onError
    });
  }

  renderLists(lists) {
    if (!lists || lists.length === 0) {
      this.renderEmptyState();
      return;
    }

    const latestList = lists.reduce((latest, current) => {
      return (current.uts > latest.uts) ? current : latest;
    });

    // Store list data for removal operations
    this.currentList = latestList;

    // Get products from listcontents array
    const products = latestList.listcontents || [];
    this.renderProducts(products);
  }

  async renderProducts(products) {
    if (products.length === 0) {
      this.renderEmptyState();
      return;
    }

    const productDataPromises = products.map(async (product) => {
      const gid = `gid://shopify/Product/${product.empi}`;
      try {
        const productData = await window.queryProduct.getProduct(gid);
        return { ...product, productData };
      } catch (error) {
        console.error(`Error fetching product ${gid}:`, error);
        return { ...product, productData: null };
      }
    });


    const productsWithData = await Promise.all(productDataPromises);

    const failedProducts = productsWithData.filter(p => !p.productData);
    if (failedProducts.length > 0 && this.currentList && this.swat) {
      failedProducts.forEach(product => {
        const productToRemove = {
          epi: product.epi,
          empi: product.empi,
          du: product.du
        };

        this.swat.deleteFromList(
          this.currentList.lid,
          productToRemove,
          () => console.log(`Removed failed product ${product.empi} from wishlist`),
          (error) => console.error(`Error removing failed product ${product.empi}:`, error)
        );
      });
    }

    // Only render products that loaded successfully
    const validProducts = productsWithData.filter(p => p.productData);
    this.renderProductGrid(validProducts);
  }

  renderProductGrid(products) {
    this.innerHTML = products.map((product, i) => {
      const data = product.productData;

      if (!data) {
        return '';
      }
      const featuredImage = data.featuredImage;
      const secondaryImage = data.images?.nodes?.[1];
      const minPrice = data.priceRange?.minVariantPrice;
      const compareAtPrice = data.compareAtPriceRange?.minVariantPrice;
      const hasDiscount = compareAtPrice && parseFloat(compareAtPrice.amount) > parseFloat(minPrice.amount);

      const selectedVariant = this.getSelectedVariant(data, product.epi);
      const variantText = this.getVariantText(selectedVariant);
      const isAvailable = selectedVariant?.availableForSale && (selectedVariant?.quantityAvailable > 0 || !selectedVariant?.currentlyNotInStock);
      const hasOnlyDefaultVariant = data.variants?.nodes?.length === 1 && data.variants.nodes[0].title === 'Default Title';

      return `
        <div class="scroll-trigger animate--slide-in h-full" style="--animation-order: ${i};">
          <div class="card-wrapper product-card-wrapper">
            <div class="card-product">
              <div class="card-product__media relative media-fit-fill">
                <div class="card-product__media-inner ${secondaryImage ? 'card-product__media-secondary-image' : ''}">
                  <a class="product-link block" href="https://${this.siteSettings.shop.domain}/products/${data.handle}?variant=${product.epi}">
                    ${featuredImage ? `
                      <responsive-image class="responsive-image product-first-image" style="--aspect-ratio:125.0%">
                        <img
                          src="${featuredImage.url}"
                          alt="${featuredImage.altText || data.title}"
                          width="${featuredImage.width || 1440}"
                          height="${featuredImage.height || 1800}"
                          loading="lazy"
                          sizes="(min-width: 1300px) calc(100vw / 4.135), (min-width: 990px) calc((100vw - 130px) / 3), (min-width: 750px) calc((100vw - 120px) / 3), calc((100vw - 35px) / 2)"
                          class="motion-reduce lazy-image img-loaded">
                      </responsive-image>
                    ` : ''}
                    ${secondaryImage ? `
                      <responsive-image class="responsive-image product-secondary-image" style="--aspect-ratio:125.0%">
                        <img
                          src="${secondaryImage.url}"
                          alt="${secondaryImage.altText || data.title}"
                          width="${secondaryImage.width || 1440}"
                          height="${secondaryImage.height || 1800}"
                          loading="lazy"
                          sizes="(min-width: 1300px) calc(100vw / 4.135), (min-width: 990px) calc((100vw - 130px) / 3), (min-width: 750px) calc((100vw - 120px) / 3), calc((100vw - 35px) / 2)"
                          class="motion-reduce lazy-image img-loaded">
                      </responsive-image>
                    ` : ''}
                  </a>
                </div>
                <button class="remove-wishlisted-product"
                  data-empi="${product.empi}"
                  data-epi="${product.epi}"
                  data-du="${product.du}">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" class="icon icon-bookmark">
                    <path d="M2.6875 1.50696H13.3125V14.0563L8 10.9514L2.6875 14.0563V1.50696Z" stroke="currentColor"/>
                  </svg>
                </button>
              </div>
              <div class="card__content pt-6">
                <div class="card__information">
                  <div class="card__heading mb-2">
                    <a href="https://${this.siteSettings.shop.domain}/products/${data.handle}?variant=${product.epi}" class="full-unstyled-link product-link truncate">
                      ${data.title}
                    </a>
                  </div>
                  <div class="card-information">
                    <div class="price${hasDiscount ? ' price--on-sale' : ''}">
                      <div class="price__container">
                        ${hasDiscount ? `
                          <div class="price__sale">
                            <s class="price-item price-item--regular text-size-tiny">
                              ${this.formatPrice(compareAtPrice.amount, compareAtPrice.currencyCode)}
                            </s>
                            <span class="price-item price-item--sale text-size-tiny">
                              ${this.formatPrice(minPrice.amount, minPrice.currencyCode)}
                            </span>
                          </div>
                        ` : `
                          <div class="price__regular">
                            <span class="price-item price-item--regular text-size-tiny">
                              ${minPrice ? this.formatPrice(minPrice.amount, minPrice.currencyCode) : ''}
                            </span>
                          </div>
                        `}
                      </div>
                    </div>

                    ${this.renderBadges(data, minPrice, compareAtPrice)}

                    ${variantText ? `
                      <div class="variant-info">
                        <span class="variant-text text-size-tiny secondary-grey">${variantText}</span>
                      </div>
                    ` : ''}
                  </div>
                  <div class="card-buttons" ${hasOnlyDefaultVariant ? 'style="grid-template-columns: 1fr;"' : ''}>
                    ${!hasOnlyDefaultVariant ? `
                      <button class="card-edit-product btn btn-outline"
                        data-product-url="/products/${data.handle}"
                        data-product-id="${product.empi}"
                        data-variant-id="${product.epi}"
                        data-list-id="${this.currentList.lid}"
                        data-has-size-guide="${Object.keys(this.siteSettings?.has_size_guide || {}).length !== 0}"
                        >
                        ${this.settings.btnEdit}
                      </button>
                    ` : ''}
                    <button class="card-add-product btn btn-primary"
                      data-variant-id="${product.epi}"
                      ${!isAvailable ? 'disabled' : ''}>
                      ${isAvailable ? this.settings.btnAdd : this.settings.btnSoldOut}
                    </button>
                  </div>
                  <div class="cart-error-message text-size-tiny error-color mt-2" style="display: none;"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      `;
    }).filter(Boolean).join('');
  }

  formatPrice(amount, currencyCode) {
    const numAmount = parseFloat(amount);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode || 'AUD',
      currencyDisplay: 'narrowSymbol'
    }).format(numAmount);
  }

  getSelectedVariant(data, epi) {
    if (!data.variants?.nodes || !epi) return null;

    return data.variants.nodes.find(variant => {
      const id = parseInt(variant.id.replace('gid://shopify/ProductVariant/', ''));
      return id === parseInt(epi);
    });
  }

  getVariantText(variant) {
    if (!variant?.selectedOptions) return '';

    const options = variant.selectedOptions
      .filter(opt => opt.name !== 'Title')
      .map(opt => opt.value).reverse();

    return options.join('&nbsp;&nbsp;|&nbsp;&nbsp;');
  }

  renderBadges(data, minPrice, compareAtPrice) {
    const badges = [];
    const isAvailable = data.availableForSale;
    const hasDiscount = compareAtPrice && parseFloat(compareAtPrice.amount) > parseFloat(minPrice.amount);

    // Sold out badge
    if (!isAvailable && this.settings.showSoldOutBadge) {
      badges.push(`<span class="badge price__badge-sold-out">${this.settings.labelSoldOut}</span>`);
    }
    // Discount badge
    else if (hasDiscount && isAvailable && this.settings.showDiscountBadge) {
      let badgeText = '';

      if (this.settings.saleBadgeType === 'text') {
        badgeText = this.settings.labelOnSale;
      } else {
        const discountValue = this.calculateDiscount(
          parseFloat(compareAtPrice.amount),
          parseFloat(minPrice.amount),
          this.settings.saleBadgeType,
          minPrice.currencyCode
        );
        badgeText = this.settings.labelSave.replace('[AMOUNT]', discountValue);
      }

      badges.push(`<span class="badge price__badge-sale">${badgeText}</span>`);
    }

    if (this.settings.cardLabelText) {
      const labelTexts = this.settings.cardLabelText.split(',').map(t => t.trim().toLowerCase());
      const productTags = data.tags || [];

      for (const tag of productTags) {
        const tagLower = tag.toLowerCase();
        for (const labelText of labelTexts) {
          if (tagLower === labelText) {
            badges.push(`<div class="badge product__badge-label">${labelText}</div>`);
            break;
          }
        }
      }
    }

    if (badges.length === 0) return '';

    // return `<div class="card__badge ${this.settings.badgePosition}">${badges.join('')}</div>`;
    return `<div class="card__badge">${badges.join('')}</div>`;
  }

  calculateDiscount(comparePrice, price, type, currencyCode) {
    if (type === 'percentage') {
      const percentage = ((comparePrice - price) / comparePrice * 100).toFixed(0);
      return `${percentage}%`;
    } else if (type === 'fixed') {
      const discount = comparePrice - price;
      return this.formatPrice(discount, currencyCode);
    }
    return '';
  }

  renderEmptyState() {
    const emptyElement = this.nextElementSibling || document.querySelector('.wishlist-empty');

    if (emptyElement) {
      emptyElement.style.display = 'block';
    }
  }

  events() {
    this.addEventListener('click', (e) => {
      const removeBtn = e.target.closest('.remove-wishlisted-product');
      if (removeBtn) {
        e.preventDefault();
        this.removeProduct(removeBtn);
        return;
      }

      const editBtn = e.target.closest('.card-edit-product');
      if (editBtn) {
        e.preventDefault();
        this.openEditModal(editBtn);
        return;
      }

      const addToCartBtn = e.target.closest('.card-add-product');
      if (addToCartBtn) {
        e.preventDefault();
        this.addToCart(addToCartBtn);
        return;
      }
    });
  }

  async openEditModal(button) {
    const productUrl = button.dataset.productUrl;
    const variantId = button.dataset.variantId;
    const productId = button.dataset.productId;
    const listId = button.dataset.listId;
    const hasSizeGuide = button.dataset.hasSizeGuide || false;

    if (!productUrl) return;

    const variantModal = document.querySelector('product-variant-modal');
    if (!variantModal) return;

    try {
      // Fetch product data from Storefront API
      const gid = `gid://shopify/Product/${productId}`;
      const productData = await window.queryProduct.getProduct(gid);

      if (!productData) return;

      // Find the selected variant
      const selectedVariant = this.getSelectedVariant(productData, variantId);

      // Open modal with wishlist mode
      variantModal.open({
        productData,
        selectedVariant,
        productUrl,
        hasSizeGuide,
        mode: 'wishlist',
        context: {
          variantId,
          productId,
          listId
        },
        onSave: ({ variant, context }) => this.saveWishlistChanges(variant, productData, context)
      });

    } catch (error) {
      console.error('Error loading wishlist edit modal:', error);
    }
  }

  async saveWishlistChanges(variant, productData, context) {
    const { variantId: oldVariantId, productId, listId } = context;

    if (!this.swat || !variant) return;

    const newVariantId = parseInt(variant.id.replace('gid://shopify/ProductVariant/', ''));

    try {
      // Only update if the variant changed
      if (newVariantId.toString() !== oldVariantId) {
        const productUrl = window.location.origin + `/products/${productData.handle}`;

        const oldProduct = {
          epi: parseInt(oldVariantId),
          empi: parseInt(productId),
          du: productUrl
        };

        const newProductData = {
          epi: newVariantId,
          empi: parseInt(productId),
          du: productUrl,
          dt: productData.title || '',
          iu: productData.featuredImage?.url || '',
          pr: parseFloat(variant.price.amount) || 0
        };

        await new Promise((resolve, reject) => {
          this.swat.deleteFromList(listId, oldProduct, () => {
            this.swat.addToWishList(newProductData,
              (response) => {
                console.log('Successfully updated wishlist item', response);
                resolve(response);
              },
              (error) => {
                console.error('Error adding to wishlist', error);
                reject(error);
              }
            );
          }, reject);
        });

        // Refresh the wishlist display
        this.fetchSwymLists();
      }

      // Close modal
      const variantModal = document.querySelector('product-variant-modal');
      if (variantModal) {
        variantModal.close();
      }

    } catch (error) {
      console.error('Error saving wishlist changes:', error);
    }
  }

  async addToCart(button) {
    const variantId = button.dataset.variantId;

    if (!variantId) {
      console.error('No variant ID found');
      return;
    }

    // Disable button and show loading state
    const originalText = button.textContent;
    button.disabled = true;
    button.innerHTML = `<div class="loading__spinner">
      <svg
        aria-hidden="true"
        focusable="false"
        class="spinner"
        viewBox="0 0 66 66"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
      </svg>
    </div>`;

    const cartDrawer = document.querySelector('cart-drawer');

    try {
      const formData = {
        items: [{
          id: variantId,
          quantity: 1
        }],
        sections: cartDrawer ? cartDrawer.getSectionsToRender().map((section) => section.id) : [],
        sections_url: window.location.pathname
      };

      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      // Check for errors in the response (out of stock, quantity limits, etc.)
      if (data.status) {
        const errorMessage = data.message || data.description || 'Unable to add to cart';
        console.error('Cart error:', errorMessage);
        button.innerHTML = this.settings.btnUnavailable;
        button.disabled = false;

        // Show error message to user
        this.showErrorMessage(errorMessage, button);
        return;
      }

      if (cartDrawer) {
        cartDrawer.renderContents(data);
      }

      button.innerHTML = originalText;
      button.disabled = false;

    } catch (error) {
      console.error('Error adding to cart:', error);
      button.innerHTML = 'ERROR';
      button.disabled = false;

    } finally {
      if (cartDrawer && cartDrawer.classList.contains('is-empty')) {
        cartDrawer.classList.remove('is-empty');
      }
    }
  }

  showErrorMessage(message, button) {
    const card = button.closest('.card-product');
    if (!card) return;

    const errorElement = card.querySelector('.cart-error-message');
    if (!errorElement) return;

    // Show error message
    errorElement.textContent = message;
    errorElement.style.display = 'block';

    setTimeout(() => {
      errorElement.style.display = 'none';
      errorElement.textContent = '';
    }, 5000);
  }

  removeProduct(button) {
    const empi = button.dataset.empi;
    const epi = button.dataset.epi;
    const du = button.dataset.du;

    if (!this.currentList || !this.swat) {
      console.error('List or Swym not initialized');
      return;
    }

    const product = {
      epi: epi,
      empi: empi,
      du: du
    };

    const onSuccess = () => {
      const card = button.closest('.scroll-trigger');
      if (card) {
        card.remove();
      }

      const remainingCards = this.querySelectorAll('.scroll-trigger');
      if (remainingCards.length === 0) {
        this.renderEmptyState();
      }
    };

    const onError = (error) => {
      console.error('Error removing product from wishlist:', error);
    };

    this.swat.deleteFromList(this.currentList.lid, product, onSuccess, onError);
  }

}

customElements.define('wishlist-product-grid', WishlistProductGrid);
